<template>
    <Dialog
        v-if="showDialog"
        :value="showDialog"
        :header-text="$t('Choose email template')"
        :width="700"
        persistent
        fixed
        @close_modal="$emit('close')"
    >
        <v-row>
            <v-col cols="12" class="py-0">
                <v-row>
                    <v-col cols="8" class="py-0 pr-1">
                        <text-field
                            :value="searchValue"
                            :placeholder="$t('Search')"
                            prepend-inner-icon="mdi-magnify"
                            @input="searchValue = $event"
                        />
                    </v-col>
                    <v-col cols="4" class="py-0 pl-1">
                        <dropdown
                            :items="filteredLanguages"
                            :selected="filterLanguage ? filteredLanguages.filter(x => x.code === filterLanguage) : []"
                            :searchable="false"
                            :placeholder="$t('All languages')"
                            name-key="codeISO2"
                            value-key="code"
                            show-selected-first
                            @input="filterLanguage = $event.code"
                        />
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12">
                <v-row
                    no-gutters
                >
                    <v-col cols="12">
                        <data-table
                            v-if="filteredTemplates.length > 0 || tableLoader.isLoading"
                            :items="filteredTemplates"
                            :headers="headers"
                            :loading="tableLoader.isLoading"
                            row-clickable
                            @click:row="onClick"
                        >
                            <template v-slot:item.locale_code="{ item }">
                                {{ formatLanguageLabel({name: item.locale_code.substring(0, 2).toUpperCase(), code: item.locale_code}) }}
                            </template>
                        </data-table>
                        <div v-else class="body-1">{{ $t("No templates found.") }}</div>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <EditEmailTemplateDialog
            v-if="editTemplate"
            :template="editTemplate"
            :errors="errors"
            :form_data="form_data"
            :show-dialog="Boolean(editTemplate)"
            :loader="editorLoader"
            :company-id="accountId"
            :override-locale-code="editLocaleCode"
            :header-text="$t('Edit email template')"
            :show-placeholders="true"
            :type="CONTENT_TEMPLATE_TYPE.EMAIL_AUTOMATION"
            :channel="CONTENT_TEMPLATE_CHANNEL.EMAIL"
            @cancel="onCancelEditTemplate"
            @save="onSaveEditTemplate"
            @delete="onDeleteTemplate"
            @attachmentUploadChange="uploadChange"
            @attachmentDeleteFile="fileUpload.delete_file"
        />
    </Dialog>
</template>

<script>
import { $t } from "@/i18n";
import { ref, watch, computed, reactive } from "@vue/composition-api";
import Dialog from "../../../components/Dialog.vue";
import EmailAutomationModule from "../../../store/modules/email_automation";
import AppModule from "../../../store/modules/app";
import Loader from "../../../utility/loader";
import EditEmailTemplateDialog from "@/views/Dialog/EditEmailTemplateDialog/Index"
import DialogModule from "../../../store/modules/dialog";
import FileUpload from "../../../composables/FileUpload";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import { CONTENT_TEMPLATE_TYPE, CONTENT_TEMPLATE_CHANNEL } from "@/helpers/constants"
import {formatLanguageLabel} from '../../../helpers/formatting'

export default {
    name: "TemplateChooseDialog",
    methods: {formatLanguageLabel},
    components: {
        Dialog,
        EditEmailTemplateDialog
    },
    props: {
        accountId: {
            type: Number,
            required: true
        },
        showDialog: {
            type: Boolean,
            required: true
        },
        clientLocaleCode: {
            type: String,
            default: null
        }
    },
    setup(props, ctx) {
        const {
            setConfirmOptions,
            openConfirmDialog,
        } = DialogModule();
        const {
            fetchEmailAutomationTemplates,
            saveEmailAutomationTemplate,
            fetchEmailAutomationTemplate,
            deleteEmailAutomationTemplate,
            templates
        } = EmailAutomationModule()
        const { languages, fetchClassifiers } = AppModule();
        const editLocaleCode = ref(null);
        const searchValue = ref("");
        const filterLanguage = ref(null);
        const tableLoader = reactive(new Loader());
        const editorLoader = reactive(new Loader());
        const editTemplate = ref(null);
        const errors = ref({});
        const task_data = ref(null)
        const form_data = ref(null)

        const filteredLanguages = computed(() => {
            let filteredLanguages = [
                {
                    codeISO2: $t('All languages'),
                    locale_code: null
                }
            ]

            filteredLanguages = filteredLanguages.concat(languages.value.map(x => {
                return {
                    ...x,
                    codeISO2: formatLanguageLabel({name: x.code.substring(0, 2).toUpperCase(), code: x.code})
                }
            }))

            return filteredLanguages
        })

        const fetchData = async function() {
            tableLoader.withLoader(async () => {
                try {
                    await fetchClassifiers({ types: ["languages"]})
                    await fetchEmailAutomationTemplates(props.accountId);

                    if (props.clientLocaleCode && filteredLanguages.value.find(x => x.code === props.clientLocaleCode)) {
                        filterLanguage.value = props.clientLocaleCode
                    }
                } catch (e) {
                    errorDialog.showError(e);
                }
            })
        }

        const onEditTemplate = async (item) => {
            try {
                editLocaleCode.value = item.locale_code
                editTemplate.value = await fetchEmailAutomationTemplate(item.templateId)
            } catch (e) {
                errors.value = e
            }
        }

        const forceDeleteTemplate = async () => {
            await editorLoader.withLoader(async () => {
                try {
                    await deleteEmailAutomationTemplate(editTemplate.value.id, 1)
                    await fetchData()
                    ctx.emit('templateDeleted', editTemplate.value)
                    editTemplate.value = null
                } catch (error) {
                    errorDialog.showError(error)
                }
            })
        }

        const onForceDeleteTemplate = (tasks_affected) => {
            const title = tasks_affected ?
                $t("Are you sure you want to delete this template? Some tasks will be affected") :
                $t("Are you sure you want to delete this template?")

            setConfirmOptions({
                title: title,
                confirm_text: $t("Delete"),
                cancel_text: $t("Cancel"),
                action: () => forceDeleteTemplate()
            });
            openConfirmDialog();
        }

        const onDeleteTemplate = async () => {
            await editorLoader.withLoader(async () => {
                try {
                    errors.value = {}
                    const {data} = await deleteEmailAutomationTemplate(editTemplate.value.id, 0)

                    onForceDeleteTemplate(data.tasks_affected)
                } catch (error) {
                    errorDialog.showError(error)
                }

            })
        }

        const fileDeleted = (deletedId) => {
            form_data.value.files = form_data.value.files.filter(x => x.id !== deletedId);
            task_data.value.files = task_data.value.files.filter(x => x.id !== deletedId);
        }
        const fileUploaded = (resp, file) => {
            if (editTemplate.value.id) {
                const index = form_data.value.files.findIndex(x => x.file_name === resp.file_name)
                form_data.value.files[index] = resp;
            }
        }
        const fileUpload = FileUpload("email_automation_template", task_data, form_data, false, fileUploaded, fileDeleted);
        const uploadChange = (e) => {
            fileUpload.add_files_to_upload(e.target.files, true);
        };

        const forceSaveEditTemplate = async (saveTemplate) => {
            try {
                await saveEmailAutomationTemplate(saveTemplate, true)
                await fetchData()
                ctx.emit('templateUpdated', editTemplate.value)
                editTemplate.value = null;
            } catch (error) {
                errors.value = error
            }
        }

        const onForceSaveTemplate = (saveTemplate, templates_affected) => {
            setConfirmOptions({
                title: $t("Are you sure you want to save this template, the following tasks will be affected?"),
                text: templates_affected.join(', '),
                confirm_text: $t("Delete"),
                cancel_text: $t("Cancel"),
                action: () => forceSaveEditTemplate(saveTemplate)
            });
            openConfirmDialog();
        }

        const onSaveEditTemplate = async (saveTemplate) => {
            errors.value = {}
            editorLoader.withLoader(async () => {
                try {
                    const { data } = await saveEmailAutomationTemplate(saveTemplate, false)
                    if (data.templates_affected) {
                        onForceSaveTemplate(saveTemplate, data.templates_affected)
                    } else {
                        await fetchData()
                        ctx.emit('templateUpdated', editTemplate.value)
                        editTemplate.value = null;
                    }

                } catch (error) {
                    console.log(error)
                    errors.value = error
                }
            })
        }

        const onCancelEditTemplate = () => {
            editTemplate.value = null
        }

        const headers = ref([
            {
                text: $t("Template name"),
                value: "template_name",
                width: '50%'
            },
            {
                text: $t("Subject"),
                value: "subject",
                width: '50%'
            },
            {
                text: $t("Language"),
                value: "locale_code",
                width: "0%",
                format: (template) => {
                    return template.locale_code.substr(0, 2)
                }
            },
            {
                value: 'actionMenu',
                sortable: false,
                width: "0%",
                actions: [
                    {
                        text: $t("Edit template"),
                        action: (item) => onEditTemplate(item)
                    }
                ]
            }
        ])

        const filteredTemplates = computed(() => {
            let filtered = []
            const search = searchValue.value.toUpperCase()

            for (let template of templates.value) {
                for (let row of template.rows) {
                    const searchSubject = row.subject.toUpperCase()
                    const searchContent = row.content.toUpperCase()
                    if (search && !(searchSubject.includes(search) || searchContent.includes(search))) {
                        continue
                    }

                    if (filterLanguage.value && row.locale_code !== filterLanguage.value) {
                        continue
                    }

                    filtered.push({
                        templateId: template.id,
                        subject: row.subject,
                        content: row.content,
                        locale_code: row.locale_code,
                        is_public: template.is_public,
                        creator: template.creator,
                        template_name: template.template_name
                    })
                }
            }

            return filtered
        })

        const onClick = (template) => {
            ctx.emit('templateChosen', template)
        }

        watch(() => [props.showDialog], () => {
            if (props.showDialog) {
                fetchData()
            }
        })

        return {
            onClick,
            headers,
            filteredTemplates,
            searchValue,
            filteredLanguages,
            filterLanguage,
            tableLoader,
            editTemplate,
            onCancelEditTemplate,
            onDeleteTemplate,
            onSaveEditTemplate,
            errors,
            form_data,
            uploadChange,
            fileUpload,
            editorLoader,
            editLocaleCode,
            CONTENT_TEMPLATE_TYPE,
            CONTENT_TEMPLATE_CHANNEL
        };
    }
};
</script>
